import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  useToast,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Radio,
  RadioGroup,
  Stack,
  Badge,
  Skeleton,
  SkeletonCircle,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, MoreHorizontal, Flag, UserX, Share2, MessageCircle, UserPlus, UserMinus } from 'lucide-react';
import { auth } from '../services/firebase';
import userService, { UserProfile } from '../services/userService';
import { 
  followUser, 
  unfollowUser, 
  isFollowing as checkIsFollowing,
  getFollowers,
  getFollowing,
  getFriends,
  isFriend,
} from '../services/socialService';
import { subscribeToLeaderboard, LeaderboardEntry } from '../services/leaderboardService';
import { ProfileAvatar } from '../components/profile';
import ChatModal from '../components/ChatModal';
import reportService, { ReportReason, REPORT_REASONS } from '../services/reportService';
import { useBlockedUsers, blockUser, unblockUser } from '../services/blockService';
import { fileToEvidenceBlob, uploadEvidence } from '../services/reportEvidenceService';

// ============================================================================
// PUBLIC PROFILE PAGE - Production Ready
// ============================================================================
// Features:
// - View other users' profiles
// - Follow/Unfollow with optimistic updates
// - Message friends (mutual follows only)
// - Report users
// - Share profile
// - Player statistics
// ============================================================================

const PublicProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const currentUserId = auth.currentUser?.uid;
  const blockedUsers = useBlockedUsers(currentUserId);
  const isBlocked = !!userId && blockedUsers.has(userId);
  const [blockBusy, setBlockBusy] = useState(false);
  const handleBlockToggle = async () => {
    if (!userId || blockBusy) return;
    setBlockBusy(true);
    const r = isBlocked ? await unblockUser(userId) : await blockUser(userId);
    setBlockBusy(false);
    if (r.success) {
      toast({
        title: isBlocked ? 'Player unblocked' : 'Player blocked',
        description: isBlocked ? undefined : 'They are muted and hidden for you at every table, and neither of you can message the other.',
        status: 'success', duration: 4000,
      });
    } else {
      toast({ title: isBlocked ? 'Could not unblock' : 'Could not block', description: r.error, status: 'error', duration: 4000 });
    }
  };
  
  // ============================================================================
  // STATE
  // ============================================================================
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [socialCounts, setSocialCounts] = useState({
    friends: 0,
    followers: 0,
    following: 0,
  });
  const [stats, setStats] = useState({
    handsWon: 0,
    rank: 0,
  });
  
  // Report modal
  const reportModal = useDisclosure();
  const [reportReason, setReportReason] = useState<ReportReason | ''>('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportEvidence, setReportEvidence] = useState<{ blob: Blob; preview: string } | null>(null);
  const handlePickEvidence = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const blob = await fileToEvidenceBlob(file);
    if (!blob) { toast({ title: 'Please choose an image', status: 'warning', duration: 3000 }); return; }
    if (reportEvidence) URL.revokeObjectURL(reportEvidence.preview);
    setReportEvidence({ blob, preview: URL.createObjectURL(blob) });
  };

  // Chat modal
  const chatModal = useDisclosure();
  const [isFriendWithUser, setIsFriendWithUser] = useState(false);

  // ============================================================================
  // REDIRECT IF VIEWING OWN PROFILE
  // ============================================================================

  useEffect(() => {
    if (userId && currentUserId && userId === currentUserId) {
      navigate('/profile', { replace: true });
    }
  }, [userId, currentUserId, navigate]);

  // ============================================================================
  // LOAD USER PROFILE
  // ============================================================================

  useEffect(() => {
    const loadProfile = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userProfile = await userService.getUserProfile(userId);
        setProfile(userProfile);

        // Load social counts in parallel
        const [followersData, followingData, friendsData] = await Promise.all([
          getFollowers(userId),
          getFollowing(userId),
          getFriends(userId),
        ]);
        
        setSocialCounts({
          followers: followersData.length,
          following: followingData.length,
          friends: friendsData.length,
        });

        // Check if current user follows this user
        if (currentUserId) {
          const [following, areFriends] = await Promise.all([
            checkIsFollowing(currentUserId, userId),
            isFriend(currentUserId, userId),
          ]);
          setIsFollowing(following);
          setIsFriendWithUser(areFriends);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast({
          title: 'Error',
          description: 'Failed to load profile',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId, currentUserId, toast]);

  // ============================================================================
  // LOAD LEADERBOARD STATS
  // ============================================================================

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToLeaderboard(
      (entries: LeaderboardEntry[]) => {
        const userEntry = entries.find(e => e.userId === userId);
        if (userEntry) {
          setStats({
            handsWon: userEntry.handsWon || 0,
            rank: userEntry.rank || 0,
          });
        }
      },
      'allTime',
      100 // Get more entries to find this user
    );

    return () => unsubscribe();
  }, [userId]);

  // ============================================================================
  // FOLLOW/UNFOLLOW - OPTIMISTIC UPDATES
  // ============================================================================

  const handleFollowToggle = async () => {
    if (!currentUserId || !userId) return;

    setFollowLoading(true);
    
    // Optimistic update
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setSocialCounts(prev => ({
      ...prev,
      followers: wasFollowing ? Math.max(0, prev.followers - 1) : prev.followers + 1,
    }));

    try {
      if (wasFollowing) {
        await unfollowUser(currentUserId, userId);
        setIsFriendWithUser(false); // No longer friends if unfollowed
        toast({
          title: 'Unfollowed',
          status: 'info',
          duration: 2000,
          position: 'top',
        });
      } else {
        await followUser(currentUserId, userId);
        // Check if now friends (mutual follow)
        const nowFriends = await isFriend(currentUserId, userId);
        setIsFriendWithUser(nowFriends);
        toast({
          title: nowFriends ? 'You are now friends!' : 'Following!',
          status: 'success',
          duration: 2000,
          position: 'top',
        });
      }
    } catch (error) {
      // Revert on error
      setIsFollowing(wasFollowing);
      setSocialCounts(prev => ({
        ...prev,
        followers: wasFollowing ? prev.followers + 1 : Math.max(0, prev.followers - 1),
      }));
      console.error('Error toggling follow:', error);
      toast({
        title: 'Error',
        description: 'Failed to update follow status',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setFollowLoading(false);
    }
  };

  // ============================================================================
  // REPORT USER
  // ============================================================================

  const handleReportSubmit = async () => {
    if (!reportReason) {
      toast({
        title: 'Please select a reason',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (!currentUserId || !userId || !profile) {
      toast({
        title: 'Error',
        description: 'Unable to submit report. Please try again.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setReportSubmitting(true);
    try {
      // Cloud Function handles getting usernames server-side for security
      let evidenceUrls: string[] = [];
      if (reportEvidence && currentUserId) {
        try { evidenceUrls = [await uploadEvidence(currentUserId, reportEvidence.blob)]; }
        catch (e) { console.warn('[evidence] upload failed, sending report without it', e); }
      }
      const result = await reportService.submitReport(
        userId,
        reportReason,
        reportDetails || undefined,
        evidenceUrls
      );
      
      toast({
        title: 'Report Submitted',
        description: result.message || 'Thank you for helping keep our community safe.',
        status: 'success',
        duration: 4000,
      });
      reportModal.onClose();
      setReportReason('');
      setReportDetails('');
      if (reportEvidence) URL.revokeObjectURL(reportEvidence.preview);
      setReportEvidence(null);
    } catch (error: any) {
      console.error('Report submission error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit report. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  // ============================================================================
  // SHARE PROFILE
  // ============================================================================

  const handleShare = async () => {
    const profileUrl = `${window.location.origin}/profile/${userId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.username}'s Profile`,
          text: `Check out ${profile?.username} on PokerSweepStars!`,
          url: profileUrl,
        });
      } catch (error) {
        // User cancelled or share failed, fall back to clipboard
        copyToClipboard(profileUrl);
      }
    } else {
      copyToClipboard(profileUrl);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Link Copied!',
      description: 'Profile link copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  // ============================================================================
  // MESSAGE USER
  // ============================================================================

  const handleMessage = () => {
    if (isFriendWithUser) {
      chatModal.onOpen();
    } else {
      toast({
        title: 'Cannot message',
        description: 'You can only message friends (mutual follows)',
        status: 'warning',
        duration: 3000,
      });
    }
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (loading) {
    return (
      <Box minH="100vh" bg="black" pb={{ base: '100px', md: '40px' }}>
        {/* Header Skeleton */}
        <HStack justify="space-between" py={4} px={4} borderBottom="1px solid" borderColor="gray.900">
          <Skeleton w="40px" h="40px" borderRadius="md" />
          <Skeleton w="100px" h="24px" />
          <Skeleton w="40px" h="40px" borderRadius="md" />
        </HStack>

        <VStack spacing={6} px={4} py={6} maxW="500px" mx="auto">
          {/* Avatar Skeleton */}
          <SkeletonCircle size="100px" />
          
          {/* Name Skeleton */}
          <VStack spacing={2}>
            <Skeleton w="150px" h="28px" />
            <Skeleton w="100px" h="20px" />
          </VStack>

          {/* Stats Skeleton */}
          <HStack spacing={8}>
            {[1, 2, 3].map(i => (
              <VStack key={i} spacing={1}>
                <Skeleton w="40px" h="24px" />
                <Skeleton w="60px" h="16px" />
              </VStack>
            ))}
          </HStack>

          {/* Buttons Skeleton */}
          <HStack spacing={3} w="100%">
            <Skeleton flex={1} h="44px" borderRadius="full" />
            <Skeleton flex={1} h="44px" borderRadius="full" />
          </HStack>
        </VStack>
      </Box>
    );
  }

  // ============================================================================
  // USER NOT FOUND
  // ============================================================================

  if (!profile) {
    return (
      <Box 
        minH="100vh" 
        bg="black" 
        display="flex" 
        flexDirection="column"
        alignItems="center" 
        justifyContent="center"
        p={4}
      >
        <Text fontSize="4xl" mb={4}></Text>

        <Text color="gray.400" fontSize="lg" mb={2}>
          User not found
        </Text>
        <Text color="gray.600" fontSize="sm" mb={6}>
          This profile may have been deleted or doesn't exist
        </Text>
        <Button
          bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
          color="white"
          _hover={{ transform: 'scale(1.02)' }}
          onClick={() => navigate(-1)}
          borderRadius="full"
          px={8}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box minH="100vh" bg="black" pb={{ base: '100px', md: '40px' }}>
      {/* Header */}
      <HStack 
        justify="space-between" 
        py={4} 
        px={4}
        borderBottom="1px solid"
        borderColor="gray.900"
        position="sticky"
        top={0}
        bg="black"
        zIndex={10}
      >
        <IconButton
          aria-label="Go back"
          icon={<ChevronLeft size={24} />}
          variant="ghost"
          color="white"
          onClick={() => navigate(-1)}
          _hover={{ bg: 'gray.800' }}
          minW="44px"
          h="44px"
        />
        
        <Text color="white" fontSize="lg" fontWeight="semibold" noOfLines={1} maxW="200px">
          {profile.username}
        </Text>
        
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="More options"
            icon={<MoreHorizontal size={24} />}
            variant="ghost"
            color="white"
            _hover={{ bg: 'gray.800' }}
            minW="44px"
            h="44px"
          />
          <MenuList bg="gray.900" borderColor="gray.700">
            <MenuItem 
              icon={<Share2 size={16} />}
              onClick={handleShare}
              bg="gray.900"
              _hover={{ bg: 'gray.800' }}
            >
              Share Profile
            </MenuItem>
            <MenuItem 
              icon={<Flag size={16} />}
              onClick={reportModal.onOpen}
              bg="gray.900"
              _hover={{ bg: 'gray.800' }}
              color="red.400"
            >
              Report User
            </MenuItem>
            <MenuItem
              icon={<UserX size={16} />}
              onClick={handleBlockToggle}
              isDisabled={blockBusy}
              bg="gray.900"
              _hover={{ bg: 'gray.800' }}
              color={isBlocked ? 'green.300' : 'orange.300'}
            >
              {isBlocked ? 'Unblock User' : 'Block User'}
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>

      <VStack spacing={6} px={4} py={6} maxW="500px" mx="auto">
        {/* Profile Header */}
        <VStack spacing={4}>
          <ProfileAvatar
            userId={userId || ''}
            photoURL={profile.photoURL}
            fullName={profile.fullName}
            username={profile.username}
            size="xl"
            editable={false}
          />
          
          <VStack spacing={1}>
            <HStack spacing={2}>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {profile.showFullName && profile.fullName 
                  ? profile.fullName 
                  : profile.username}
              </Text>
              {profile.kycStatus === 'verified' && (
                <Badge colorScheme="green" fontSize="xs" borderRadius="full">
                   Verified

                </Badge>
              )}
            </HStack>
            <Text color="gray.500" fontSize="sm">
              @{profile.username}
            </Text>
          </VStack>

          {/* Stats Row */}
          <HStack spacing={8} pt={2}>
            <VStack spacing={0}>
              <Text color="white" fontSize="lg" fontWeight="bold">
                {stats.handsWon || profile.stats?.handsWon || 0}
              </Text>
              <Text color="gray.500" fontSize="xs">Wins</Text>
            </VStack>
            <VStack spacing={0}>
              <Text color="white" fontSize="lg" fontWeight="bold">
                {socialCounts.friends}
              </Text>
              <Text color="gray.500" fontSize="xs">Friends</Text>
            </VStack>
            <VStack spacing={0}>
              <Text color="white" fontSize="lg" fontWeight="bold">
                {socialCounts.followers}
              </Text>
              <Text color="gray.500" fontSize="xs">Followers</Text>
            </VStack>
          </HStack>

          {/* Bio */}
          {profile.bio && (
            <Text 
              color="gray.400" 
              fontSize="sm" 
              textAlign="center" 
              maxW="300px"
              pt={2}
              fontStyle="italic"
            >
              "{profile.bio}"
            </Text>
          )}

          {/* Action Buttons */}
          <HStack spacing={3} pt={4} w="100%">
            <Button
              flex={1}
              bg={isFollowing ? 'transparent' : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'}
              color={isFollowing ? 'gray.400' : 'white'}
              border={isFollowing ? '1px solid' : 'none'}
              borderColor="gray.600"
              leftIcon={isFollowing ? <UserMinus size={18} /> : <UserPlus size={18} />}
              _hover={{ 
                bg: isFollowing ? 'red.900' : 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
                color: isFollowing ? 'red.400' : 'white',
                borderColor: isFollowing ? 'red.600' : 'transparent',
                transform: 'scale(1.02)',
              }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={handleFollowToggle}
              isLoading={followLoading}
              borderRadius="full"
              fontWeight="bold"
              h="44px"
            >
              {isFollowing ? 'Unfollow' : 'Follow'}
            </Button>
            <Button
              flex={1}
              bg={isFriendWithUser ? 'transparent' : 'gray.800'}
              color={isFriendWithUser ? 'cyan.400' : 'gray.400'}
              border="1px solid"
              borderColor={isFriendWithUser ? 'cyan.600' : 'gray.600'}
              leftIcon={<MessageCircle size={18} />}
              _hover={{ 
                bg: isFriendWithUser ? 'cyan.900' : 'gray.700', 
                transform: 'scale(1.02)',
              }}
              _active={{ transform: 'scale(0.98)' }}
              borderRadius="full"
              fontWeight="bold"
              h="44px"
              onClick={handleMessage}
              opacity={isFriendWithUser ? 1 : 0.6}
            >
              Message
            </Button>
          </HStack>
          
          {/* Friend hint */}
          {!isFriendWithUser && isFollowing && (
            <Text color="gray.600" fontSize="xs" textAlign="center">
               You can message when they follow you back

            </Text>
          )}
        </VStack>

        {/* Player Stats Card */}
        <Box
          w="100%"
          bg="#1C1C1E"
          borderRadius="xl"
          p={4}
        >
          <Text color="gray.400" fontSize="sm" fontWeight="semibold" mb={4}>
            Player Statistics
          </Text>
          <VStack spacing={3} align="stretch">
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="sm">Games Played</Text>
              <Text color="white" fontWeight="bold">
                {profile.stats?.gamesPlayed || 0}
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="sm">Hands Won</Text>
              <Text color="green.400" fontWeight="bold">
                {stats.handsWon || profile.stats?.handsWon || 0}
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="sm">Total Won</Text>
              <Text color="orange.400" fontWeight="bold">
                ${(profile.stats?.totalWinnings || 0).toLocaleString()}
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="sm">Leaderboard Rank</Text>
              <Text color="yellow.400" fontWeight="bold">
                {stats.rank ? `#${stats.rank}` : '-'}
              </Text>
            </HStack>
          </VStack>
        </Box>

        {/* Mutual Friends Indicator */}
        {isFriendWithUser && (
          <Box
            w="100%"
            bg="cyan.900"
            border="1px solid"
            borderColor="cyan.700"
            borderRadius="xl"
            p={4}
            textAlign="center"
          >
            <HStack justify="center" spacing={2}>
              <Text fontSize="lg"></Text>

              <Text color="cyan.200" fontWeight="semibold">
                You and {profile.username} are friends
              </Text>
            </HStack>
          </Box>
        )}

        {/* Member Since */}
        <Text color="gray.600" fontSize="xs">
          Member since {profile.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
        </Text>
      </VStack>

      {/* Report Modal */}
      <Modal isOpen={reportModal.isOpen} onClose={reportModal.onClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="#1C1C1E" mx={4} borderRadius="xl">
          <ModalHeader color="white" borderBottom="1px solid" borderColor="gray.800">
            Report User
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody pb={6}>
            {/* User being reported */}
            <Box
              bg="gray.900"
              p={3}
              borderRadius="lg"
              border="1px solid"
              borderColor="red.900"
              mb={4}
            >
              <HStack>
                <ProfileAvatar
                  userId={userId || ''}
                  photoURL={profile.photoURL}
                  username={profile.username}
                  size="sm"
                  editable={false}
                />
                <Text color="white" fontWeight="semibold">
                  {profile.username}
                </Text>
              </HStack>
            </Box>

            <Text color="orange.400" fontWeight="semibold" mb={3}>
              Why do you want to report this user?
            </Text>

            <RadioGroup value={reportReason} onChange={(val) => setReportReason(val as ReportReason)}>
              <Stack spacing={3}>
                {REPORT_REASONS.map((reason) => (
                  <Box
                    key={reason.value}
                    p={3}
                    borderBottom="1px solid"
                    borderColor="gray.800"
                    cursor="pointer"
                    onClick={() => setReportReason(reason.value)}
                    _hover={{ bg: 'gray.900' }}
                    borderRadius="md"
                    bg={reportReason === reason.value ? 'gray.800' : 'transparent'}
                  >
                    <HStack justify="space-between">
                      <HStack spacing={2}>
                        <Text>{reason.icon}</Text>
                        <Text color="white" fontSize="sm">
                          {reason.label}
                        </Text>
                      </HStack>
                      <Radio 
                        value={reason.value} 
                        colorScheme="orange"
                        borderColor="gray.600"
                      />
                    </HStack>
                  </Box>
                ))}
              </Stack>
            </RadioGroup>

            <Box mb={3}>
              <Text color="gray.400" fontSize="sm" mb={2}>Screenshot (optional, but it helps a lot)</Text>
              {reportEvidence ? (
                <HStack spacing={3}>
                  <img src={reportEvidence.preview} alt="Attached" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid #334155' }} />
                  <Text color="gray.300" fontSize="sm">Screenshot attached</Text>
                  <Button size="xs" variant="ghost" color="gray.400" onClick={() => { URL.revokeObjectURL(reportEvidence.preview); setReportEvidence(null); }}>Remove</Button>
                </HStack>
              ) : (
                <Button as="label" size="sm" variant="outline" colorScheme="cyan" cursor="pointer">
                  Add a screenshot
                  <input type="file" accept="image/*" hidden onChange={handlePickEvidence} />
                </Button>
              )}
            </Box>
            <Textarea
              placeholder="Additional details (optional)"
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              mt={4}
              bg="gray.900"
              border="1px solid"
              borderColor="gray.700"
              color="white"
              _placeholder={{ color: 'gray.500' }}
              _focus={{ borderColor: 'orange.400' }}
            />

            <Button
              w="100%"
              mt={4}
              bg="linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
              color="white"
              _hover={{ transform: 'scale(1.02)' }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={handleReportSubmit}
              isLoading={reportSubmitting}
              isDisabled={!reportReason}
              h="44px"
            >
              Submit Report
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Chat Modal */}
      <ChatModal
        isOpen={chatModal.isOpen}
        onClose={chatModal.onClose}
        targetUserId={userId || ''}
        targetUsername={profile?.username || ''}
        targetPhotoURL={profile?.photoURL}
      />
    </Box>
  );
};

export default PublicProfilePage;